import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import SellConfigBand from '../components/proposalGrid/SellConfigBand';
import Worksheet from '../components/proposalGrid/Worksheet';
import OutcomeCompletenessPanel from '../components/verification/OutcomeCompletenessPanel';
import { CloseIcon } from '../components/icons';
import { proposalsApi, signingApi, proposalSuggestionsApi } from '../utils/api';

// PRD-024 — spreadsheet-style Proposal editor, per-Outcome Sell Config. Each
// Outcome row carries its OWN Sell Unit Name · UoM · Price · Qty → its own
// Revenue; the parent totals are the SUM of the children (no allocation %). A
// derived hero strip (Agreement value · est cost · projected margin) sits above
// a worksheet of Outcomes — each an editable economics line, a seller-only
// Estimated Cost panel (live projected margin), and the full 5-block spec
// table — closed by a footer rollup + a completeness sign gate (FR-7). The
// buyer mirror is PublicProposal.js (read-only, cost hidden).

function apiErrorMessage(err, fallback) {
  return (
    err.response?.data?.message ||
    err.response?.data?.error ||
    err.response?.data?.errors?.[0]?.msg ||
    fallback
  );
}


// PRD-031 F7 — signing readiness as a TRANSIENT surface (ratified mockup):
// it slides in bottom-right when readiness changes or a Sign attempt fails,
// is dismissible, and never occupies permanent layout. Content unchanged
// from the retired permanent card: named incomplete Outcomes, rate-coverage
// gaps, open suggestions, and the server's can't-sign errors.
function TransientReadiness({ open, onClose, allClear, incompleteOutcomes, hasCostGap, costGapLabel, openSuggestionCount, signErrors, isSeller }) {
  useEffect(() => {
    if (open && allClear) {
      const t = setTimeout(onClose, 6000);   // the all-clear auto-dismisses
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, allClear, onClose]);
  if (!open) return null;

  const blocks = [];
  if (incompleteOutcomes.length > 0) {
    blocks.push({
      chip: 'Incomplete', tone: 'bg-coral-500 text-white',
      ref: incompleteOutcomes.map((o) => o.name).join(' · '),
      why: `${incompleteOutcomes.length === 1 ? 'This Outcome needs' : 'These Outcomes need'} a price and a unit before signing.`,
    });
  }
  if (hasCostGap) {
    blocks.push({
      chip: 'Missing a detail', tone: 'bg-amber-400 text-amber-950',
      ref: costGapLabel ? `No rate covers ${costGapLabel}` : 'A rate-schedule gap',
      why: 'An Outcome’s active window includes dates its rate schedule doesn’t cover, so the estimate can’t be frozen.',
      action: isSeller ? { label: 'Extend the rate window', to: '/cost-input-types' } : null,
    });
  }
  if (openSuggestionCount > 0) {
    blocks.push({
      chip: 'Suggestions', tone: 'bg-amber-400 text-amber-950',
      ref: `${openSuggestionCount} open suggestion${openSuggestionCount === 1 ? '' : 's'}`,
      why: isSeller ? 'Accept or reject to resolve; signing prompts to abandon them.' : 'Resolve them, or abandon them when you sign.',
    });
  }
  for (const e of signErrors || []) {
    blocks.push({
      chip: 'Can’t sign yet', tone: 'bg-coral-500 text-white',
      ref: e.outcome_name ? `${e.outcome_name}${e.block_type ? ` · ${e.block_type.replace('_', ' ')}` : ''}` : 'Signing',
      why: `${e.message}${e.blocked_on_side ? ` (needs the ${e.blocked_on_side === 'buyer' ? 'client' : 'agency'})` : ''}`,
    });
  }

  if (blocks.length === 0 && !allClear) {
    // The zero-outcome hard block (nothing else can name itself).
    blocks.push({
      chip: 'Incomplete', tone: 'bg-coral-500 text-white',
      ref: 'No Outcomes yet',
      why: 'Add an Outcome to sign — a Proposal needs at least one priced Outcome.',
    });
  }

  return (
    <div className={`fixed right-6 bottom-6 z-[70] w-[420px] max-w-[calc(100vw-48px)] bg-white border rounded-2xl shadow-none ${allClear ? 'border-verified' : 'border-gray-300'}`}
      style={{ boxShadow: '0 18px 44px rgba(19,19,19,.18)' }} data-testid="readiness-transient">
      <div className={`flex items-center gap-2.5 px-4 py-3 border-b ${allClear ? 'border-verified/40' : 'border-gray-200'}`}>
        <span className={`text-[13.5px] font-bold flex-1 ${allClear ? 'text-verified' : 'text-gray-900'}`}>
          {allClear ? 'Ready to sign' : `Not ready to sign — ${blocks.length} thing${blocks.length === 1 ? '' : 's'} need${blocks.length === 1 ? 's' : ''} attention`}
        </span>
        <button type="button" onClick={onClose} aria-label="Close" className="text-gray-600 hover:text-gray-900 p-1 rounded-md"><CloseIcon /></button>
      </div>
      <div className="px-4 py-3 text-[12.5px] text-gray-700">
        {allClear ? (
          <p>Every Outcome has a price and a unit{isSeller ? ', and estimated costs have rate coverage for the full term' : ''}. Nothing blocks signing.</p>
        ) : (
          blocks.map((b, i) => (
            <div key={i} className={`flex gap-2.5 items-start py-2.5 ${i > 0 ? 'border-t border-gray-200' : ''}`}>
              <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-md whitespace-nowrap mt-0.5 ${b.tone}`}>{b.chip}</span>
              <div className="min-w-0">
                <div className="font-semibold text-gray-900">{b.ref}</div>
                <div className="mt-0.5">{b.why}</div>
                {b.action && (
                  <button type="button" className="text-primary-600 font-semibold text-[12px] mt-1" onClick={() => { window.location.assign(b.action.to); }}>{b.action.label}</button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function ProposalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [economics, setEconomics] = useState(null);
  const [delivByOutcome, setDelivByOutcome] = useState({}); // outcomeId → first deliverable id
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draftName, setDraftName] = useState(null); // add-outcome form (name only — never asks %)
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [removeOutcomeId, setRemoveOutcomeId] = useState(null);
  // PRD-027 WU-C/WU-D — buyer suggestions + signing readiness.
  const [suggestions, setSuggestions] = useState([]);
  const [signErrors, setSignErrors] = useState(null);
  const [showAbandon, setShowAbandon] = useState(false);
  // PRD-031 F7 — the transient readiness surface shows on readiness CHANGE
  // and on a failed Sign attempt; never permanently.
  const [readinessOpen, setReadinessOpen] = useState(false);
  const readinessSig = useRef(null);

  const refetch = useCallback(async () => {
    try {
      const [pRes, eRes, sRes] = await Promise.all([
        proposalsApi.get(id), proposalsApi.economics(id),
        proposalSuggestionsApi.list(id).catch(() => ({ data: { suggestions: [] } })),
      ]);
      setProposal(pRes.data.proposal);
      setEconomics(eRes.data.economics);
      setSuggestions(sRes.data.suggestions || []);
      const map = {};
      for (const o of (pRes.data.outcomes || [])) {
        const first = (o.dealDeliverables || [])[0];
        if (first) map[o.id] = first.id;
      }
      setDelivByOutcome(map);
      setError(null);
    } catch (err) {
      setError(err.response?.status === 404 ? 'not_found' : 'error');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refetch(); }, [refetch]);

  // PRD-031 F7 — the transient readiness surface shows when the blocker set
  // CHANGES (a blocker appears or resolves), never on first paint. This hook
  // is unconditional (it precedes every early return); it no-ops until the
  // data is loaded.
  useEffect(() => {
    if (!proposal || !economics || proposal.status === 'signed') return;
    const sig = JSON.stringify([
      (economics.outcomes || []).filter((o) => !o.is_complete).map((o) => o.outcome_id),
      (economics.outcomes || []).some((o) => o.has_cost_gap),
      suggestions.filter((sg) => sg.status === 'open').length,
    ]);
    if (readinessSig.current === null) { readinessSig.current = sig; return; }
    if (readinessSig.current !== sig) {
      readinessSig.current = sig;
      setSignErrors(null);
      setReadinessOpen(true);
    }
  }, [proposal, economics, suggestions]);

  const isSeller = proposal && proposal.myRole !== 'buyer';
  const isDraft = proposal && !['signed', 'cancelled'].includes(proposal.status);
  const editable = isSeller && isDraft;

  // Merge the economics payload (canonical numbers) with the per-Outcome
  // deliverable id (for the spec panel). PRD-024 — per-Outcome Sell Config
  // fields are the inputs; revenue is their derived price × qty.
  const rows = (economics?.outcomes || []).map((o) => ({
    outcomeId: o.outcome_id,
    name: o.name,
    revenueCents: o.revenue_cents,
    sellUnitName: o.sell_unit_name || '',
    sellUnitOfMeasureId: o.sell_unit_of_measure_id || null,
    sellUnitNoun: o.sell_unit_noun || '',
    sellPricePerUnitCents: o.sell_price_per_unit_cents != null ? o.sell_price_per_unit_cents : null,
    sellQuantity: o.sell_quantity != null ? o.sell_quantity : null,
    estimatedCostCents: o.estimated_cost_cents || 0,
    projectedMarginCents: o.projected_margin_cents || 0,
    projectedMarginPct: o.projected_margin_pct || 0,
    costLines: o.cost_lines || [],
    // PRD-027 WU-G — the Outcome's active (delivery/consumption) window.
    activeFrom: o.active_from || null,
    activeTo: o.active_to || null,
    hasCostGap: !!o.has_cost_gap,
    costGapLabel: o.cost_gap_label || '',
    deliverableId: delivByOutcome[o.outcome_id] || null,
  }));

  // PRD-024 — persist a per-Outcome Sell Config field. Money/qty commits arrive
  // on blur from the worksheet input (debounced by the user finishing typing),
  // matching the old SellConfigBand's on-blur persist; UoM commits on pick.
  const saveSellField = async (outcomeId, payload) => {
    try { await proposalsApi.updateOutcome(id, outcomeId, payload); await refetch(); }
    catch (err) { alert(apiErrorMessage(err, 'Failed to save Sell Configuration')); }
  };

  const addOutcome = async () => {
    if (!draftName?.trim()) return;
    setBusy(true);
    try {
      await proposalsApi.addOutcome(id, { name: draftName.trim() });
      setDraftName(null);
      await refetch();
    } catch (err) {
      alert(apiErrorMessage(err, 'Failed to add outcome'));
    } finally { setBusy(false); }
  };

  const removeOutcome = async () => {
    if (!removeOutcomeId) return;
    try { await proposalsApi.deleteOutcome(id, removeOutcomeId); setRemoveOutcomeId(null); await refetch(); }
    catch (err) { alert(apiErrorMessage(err, 'Failed to remove outcome')); setRemoveOutcomeId(null); }
  };

  const addCost = async (outcomeId, body) => {
    try { await proposalsApi.addCostEstimate(id, outcomeId, body); await refetch(); }
    catch (err) { alert(apiErrorMessage(err, 'Failed to add cost estimate')); }
  };
  const removeCost = async (outcomeId, lineId) => {
    try { await proposalsApi.deleteCostEstimate(id, outcomeId, lineId); await refetch(); }
    catch (err) { alert(apiErrorMessage(err, 'Failed to remove cost estimate')); }
  };

  const deleteProposal = async () => {
    setDeleting(true);
    try { await proposalsApi.delete(id); navigate('/proposals'); }
    catch (err) { alert(apiErrorMessage(err, 'Failed to delete proposal')); setDeleting(false); setConfirmDelete(false); }
  };

  // PRD-027 WU-C — buyer-suggestion handlers (create/comment/accept/reject/withdraw).
  const suggestionHandlers = {
    onCreate: async (target, proposed, comment) => {
      await proposalSuggestionsApi.create(id, {
        target_type: target.type, target_outcome_id: target.outcomeId || null,
        target_deliverable_id: target.deliverableId || null, target_block_type: target.blockType || null,
        proposed_value: proposed, comment: comment || undefined,
      });
      await refetch();
    },
    onComment: async (sid, body) => { await proposalSuggestionsApi.comment(id, sid, { body }); await refetch(); },
    onAccept: async (sid) => { await proposalSuggestionsApi.accept(id, sid); await refetch(); },
    onReject: async (sid) => { await proposalSuggestionsApi.reject(id, sid); await refetch(); },
    onWithdraw: async (sid) => { await proposalSuggestionsApi.withdraw(id, sid); await refetch(); },
  };

  const doSign = async () => {
    setBusy(true); setSignErrors(null);
    try {
      const res = await signingApi.sign(id);
      if (res.data.agreement) { navigate(`/agreements/${res.data.agreement.id}`); return; }
      await refetch();
    } catch (err) {
      // PRD-031 F7 — a failed Sign raises the transient readiness surface
      // with the server's specific blockers.
      const data = err.response?.data;
      if (data?.errors) setSignErrors(data.errors);
      else if (data?.error_code === 'no_valid_rate_for_window') setSignErrors([{ code: 'no_valid_rate_for_window', message: data.message, outcome_name: data.outcome_name, blocked_on_side: 'seller' }]);
      // PRD-028 F2 — zero-length / inverted Outcome active window.
      else if (data?.error_code === 'invalid_active_window') setSignErrors([{ code: 'invalid_active_window', message: data.message, outcome_name: data.outcome_name, blocked_on_side: 'seller' }]);
      else alert(apiErrorMessage(err, 'Failed to sign'));
      if (err.response?.data) setReadinessOpen(true);
    } finally { setBusy(false); }
  };

  // WU-D — open Suggestions are a SOFT blocker: the buyer may click Sign and is
  // offered "abandon all & sign". Hard blockers disable the button entirely.
  const sign = async () => {
    // Hard blockers: the click raises the readiness surface (the specifics,
    // one click, no ceremony) — the server gate stays authoritative.
    if (hardBlocked) { setSignErrors(null); setReadinessOpen(true); return; }
    if (openSuggestionCount > 0) { setShowAbandon(true); return; }
    await doSign();
  };
  const abandonAndSign = async () => {
    setBusy(true);
    try { await proposalSuggestionsApi.abandonAll(id); setShowAbandon(false); await refetch(); await doSign(); }
    catch (err) { alert(apiErrorMessage(err, 'Failed to abandon suggestions')); setBusy(false); }
  };

  if (loading) return <Layout><div className="max-w-5xl mx-auto card p-6 animate-pulse h-48" /></Layout>;
  if (error === 'not_found') return <Layout><div className="max-w-5xl mx-auto card p-8 text-center text-gray-600">Proposal not found.</div></Layout>;
  if (error || !proposal) return <Layout><div className="max-w-5xl mx-auto card p-8 text-center text-gray-600">Failed to load this Proposal.</div></Layout>;

  const mySigned = isSeller ? proposal.seller_signed_at : proposal.buyer_signed_at;
  // PRD-024 FR-7 — signable when every Outcome has a complete Sell Config
  // (server-derived flag). Replaces the retired 100%-allocation gate.
  const signBlocked = !economics?.signable;
  const isBuyer = !isSeller;
  const mySide = isSeller ? 'seller' : 'buyer';
  // PRD-027 WU-C/WU-D readiness.
  const openSuggestionCount = suggestions.filter((s) => s.status === 'open').length;
  const hasCostGap = rows.some((r) => r.hasCostGap);
  const costGapLabel = (rows.find((r) => r.hasCostGap && r.costGapLabel) || {}).costGapLabel || '';
  // Hard blockers (incomplete sell config, an uncovered cost window) disable
  // Sign; open Suggestions do NOT (soft — abandon-all confirm on click).
  const hardBlocked = signBlocked || hasCostGap;
  const canSuggest = isBuyer && proposal.status !== 'signed' && !!proposal.shared_with_buyer_at;
  const incompleteOutcomes = (economics?.outcomes || []).filter((o) => !o.is_complete);
  const allClear = !hardBlocked && openSuggestionCount === 0 && !(signErrors && signErrors.length);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="page-header">{proposal.name}</h1>
            <p className="text-gray-600 mt-1 text-sm">
              {proposal.counterparty?.name ? `with ${proposal.counterparty.name} · ` : ''}
              <span className="capitalize">{proposal.status}</span>
              {proposal.shared_with_buyer_at && proposal.status !== 'signed' ? ' · shared with the client' : ''}
            </p>
          </div>
          {/* PRD-026 F1 — items-start keeps Delete and Sign on a shared top
              edge; the conditional helper below the Sign button never
              displaces either button. */}
          <div className="flex items-start gap-3">
            {isSeller && proposal.status !== 'signed' && (
              <button className="btn-secondary text-red-600 hover:bg-red-50" onClick={() => setConfirmDelete(true)}>Delete</button>
            )}
            {proposal.status === 'signed' ? (
              <div className="text-right">
                <button className="btn-primary" onClick={() => navigate('/agreements')}>View Agreement →</button>
                <p className="text-xs text-gray-600 mt-1">Signed Proposals are immutable and become Agreements.</p>
              </div>
            ) : (
              <div className="text-right">
                <button className="btn-primary" onClick={sign} disabled={busy || mySigned}>
                  {mySigned ? 'Signed — awaiting counterparty' : (isSeller ? 'Sign (shares with the client)' : 'Sign')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Derived hero strip — Agreement value (Σ children) · est cost · proj margin */}
        <SellConfigBand
          agreementValueCents={economics?.agreement_value_cents || 0}
          estimatedCostCents={economics?.totals?.estimated_cost_cents || 0}
          projectedMarginCents={economics?.totals?.projected_margin_cents || 0}
          projectedMarginPct={economics?.totals?.projected_margin_pct || 0}
          outcomeCount={economics?.sell_config?.outcome_count || rows.length}
          distinctUnitCount={economics?.sell_config?.distinct_unit_count || 0}
          showCost={!!economics?.cost_side_visible}
          isBuyer={isBuyer}
          stage="proposal"
        />

        {/* PRD-030 WU-12 — before-signing outcome-completeness panel:
            Verdict flags any outcome it can't confirm once delivery
            starts, with deep-link CTAs into the worksheet rows below. */}
        {proposal.status !== 'signed' && proposal.status !== 'cancelled' && (
          <OutcomeCompletenessPanel
            proposalId={id}
            rows={rows}
            isSeller={isSeller}
            isSignedOrCancelled={!isDraft}
          />
        )}

        {/* The worksheet */}
        <Worksheet
          proposalId={id}
          rows={rows}
          showCost={!!economics?.cost_side_visible}
          editable={editable}
          isBuyer={isBuyer}
          actorSide={isSeller ? 'seller' : 'buyer'}
          totals={{
            revenueCents: economics?.totals?.revenue_cents || 0,
            estimatedCostCents: economics?.totals?.estimated_cost_cents || 0,
            projectedMarginCents: economics?.totals?.projected_margin_cents || 0,
            projectedMarginPct: economics?.totals?.projected_margin_pct || 0,
          }}
          onSellFieldChange={saveSellField}
          onAddOutcome={() => setDraftName('')}
          onRemoveOutcome={(oid) => setRemoveOutcomeId(oid)}
          onAddCost={addCost}
          onRemoveCost={removeCost}
          suggestions={suggestions}
          suggestionHandlers={suggestionHandlers}
          mySide={mySide}
          canSuggest={canSuggest}
        />
      </div>

      {/* Add-outcome form (name only — % is set in the worksheet cell) */}
      {draftName !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
          <div className="card max-w-md w-full p-6 pointer-events-auto" style={{ marginTop: 0 }}>
            <h3 className="text-lg font-semibold text-gray-900">Add an Outcome</h3>
            <label className="label mt-3">Outcome name *</label>
            <input className="input" autoFocus placeholder="e.g., 5,000 verified app installs"
              value={draftName} onChange={(e) => setDraftName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && draftName.trim()) addOutcome(); }} />
            <p className="text-xs text-gray-600 mt-1">Set its sell unit, unit of measure, price, and quantity in the worksheet row.</p>
            <div className="flex justify-end gap-3 mt-6">
              <button className="btn-secondary" onClick={() => setDraftName(null)} disabled={busy}>Cancel</button>
              <button className="btn-primary" onClick={addOutcome} disabled={busy || !draftName.trim()}>{busy ? 'Adding…' : 'Add Outcome'}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this Proposal?"
        message={`This permanently removes “${proposal.name}” for both you and ${proposal.counterparty?.name || 'the client'}. This cannot be undone.`}
        confirmLabel="Delete Proposal"
        busy={deleting}
        onConfirm={deleteProposal}
        onCancel={() => setConfirmDelete(false)}
      />
      <ConfirmDialog
        open={!!removeOutcomeId}
        title="Remove this Outcome?"
        message="This removes the Outcome and its deliverable, spec, and cost estimates from the Proposal."
        confirmLabel="Remove Outcome"
        onConfirm={removeOutcome}
        onCancel={() => setRemoveOutcomeId(null)}
      />
      {/* PRD-031 F7 — the transient readiness surface (never a permanent card). */}
      {proposal.status !== 'signed' && (
        <TransientReadiness
          open={readinessOpen}
          onClose={() => setReadinessOpen(false)}
          allClear={allClear}
          incompleteOutcomes={incompleteOutcomes}
          hasCostGap={hasCostGap}
          costGapLabel={costGapLabel}
          openSuggestionCount={openSuggestionCount}
          signErrors={signErrors}
          isSeller={isSeller}
        />
      )}

      {/* PRD-027 WU-C/WU-D — sign with open suggestions → abandon all & sign. */}
      <ConfirmDialog
        open={showAbandon}
        title="Sign with open suggestions?"
        message={`You have ${openSuggestionCount} open suggestion${openSuggestionCount === 1 ? '' : 's'}. Signing now abandons (closes) ${openSuggestionCount === 1 ? 'it' : 'all of them'} and creates the immutable Agreement at the currently-accepted terms. This can't be undone.`}
        confirmLabel="Abandon all & sign"
        busy={busy}
        onConfirm={abandonAndSign}
        onCancel={() => setShowAbandon(false)}
      />
    </Layout>
  );
}
